import { getTown } from '@wyn/content';
import type { SanctionView } from '@wyn/contracts';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { ReportButton } from '../components/Report.tsx';
import { sanctionUntil } from '../components/Sanction.tsx';
import { Callout, PageTitle } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { UI } from '../icons/ui-icons.ts';
import { chatQuery, useSay, useSetChat } from '../lib/queries.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const MAX = 280;
const hhmm = (at: number) => new Date(at).toLocaleTimeString('en-GB', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' });

/** Talk: the realm hears everyone; a town hears those standing in it. Lines are kept for 30 days, and each can be reported. */
export function Chat() {
  const me = useMe();
  const [pick, setPick] = useState<'realm' | 'town'>('town');
  if (me.isPending) return <Loading />;
  const p = me.data?.player;
  if (!p) return <Navigate to="/" />;
  const onRoad = p.status.kind === 'travelling';
  const channel = pick === 'town' && !onRoad ? `town:${p.town}` : 'realm';
  const town = getTown(p.town);

  return (
    <GameShell me={me.data!} skew={me.skew}>
      <section className="panel space-y-3">
        <PageTitle icon={UI.talk}>Talk</PageTitle>
        {p.chat === 'off' ? <ChatOff /> : (
        <>
        <div role="tablist" aria-label="Channel" className="flex gap-2">
          <button type="button" role="tab" aria-selected={channel !== 'realm'} disabled={onRoad} className={channel !== 'realm' ? 'btn' : 'btn-quiet'} onClick={() => setPick('town')}>
            <Icon name={town.icon} className="mr-1" />{town.name}
          </button>
          <button type="button" role="tab" aria-selected={channel === 'realm'} className={channel === 'realm' ? 'btn' : 'btn-quiet'} onClick={() => setPick('realm')}>
            <Icon name={UI.realm} className="mr-1" />The Realm
          </button>
        </div>
        <p className="text-sm text-ink-faint">
          {channel === 'realm' ? 'Every soul in the realm hears this, on the road or in a cell.' : onRoad ? '' : `Only those in ${town.name} hear this.`}
          {onRoad && ' On the road, only the realm can hear you.'}
        </p>
        <Channel key={channel} channel={channel} myId={p.id} label={channel === 'realm' ? 'The Realm' : town.name} silence={p.sanction?.kind === 'mute' ? p.sanction : null} />
        </>
        )}
      </section>
    </GameShell>
  );
}

/** Chat turned off: nothing is shown or pushed until the player turns it back on (here, or on the character sheet). */
function ChatOff() {
  const set = useSetChat();
  return (
    <div className="space-y-2">
      <Callout tone="muted" role="status">Your chat is off. You see nothing anyone says, and nobody hears you.</Callout>
      <button type="button" className="btn" disabled={set.isPending} onClick={() => set.mutate({ chat: 'on' })}>Turn chat on</button>
      <ErrorNotice error={set.error} />
    </div>
  );
}

function Channel({ channel, myId, label, silence }: { channel: string; myId: string; label: string; silence: SanctionView | null }) {
  const q = useQuery(chatQuery(channel));
  const say = useSay();
  const [text, setText] = useState('');
  const end = useRef<HTMLLIElement>(null);
  const lines = q.data?.messages ?? [];
  const last = lines[lines.length - 1]?.id;
  // Braces matter: newer browsers return a promise from scrollIntoView, and an effect may return only a cleanup.
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: 'nearest' });
  }, [last]);

  return (
    <div className="space-y-2">
      <ol role="log" aria-label={`Talk in ${label}`} className="h-[28rem] space-y-1 overflow-y-auto well p-3">
        {lines.length === 0 && <li className="text-ink-faint">{q.isPending ? 'Listening…' : 'Nobody has said a word.'}</li>}
        {lines.map((m) => (
          <li key={m.id} className="grid grid-cols-[3rem_1fr_1rem] gap-2 text-sm">
            <time className="font-mono text-xs leading-5 text-ink-faint" dateTime={new Date(m.at).toISOString()}>{hhmm(m.at)}</time>
            {m.removed ? (
              <span className="italic text-ink-faint">— removed by a steward —</span>
            ) : (
            <span className="break-words">
              <span className={m.playerId === myId ? 'text-gilt' : 'text-ochre'}>{m.name}</span>
              <span className="text-ink-faint">: </span>
              {m.text}
            </span>
            )}
            {m.playerId !== myId && !m.removed && <ReportButton target={{ kind: 'chat', channel: m.channel, lineId: m.id }} what={`${m.name}’s line`} who={m.name} className="text-xs leading-5" />}
          </li>
        ))}
        <li ref={end} aria-hidden />
      </ol>
      {silence ? (
        <Callout tone="bad" role="status">
          A steward has silenced you {sanctionUntil(silence)}: {silence.reason} You can still read what others say.
        </Callout>
      ) : (
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) say.mutate({ channel, text }, { onSuccess: () => setText('') });
        }}
      >
        <input
          className="field min-w-0 flex-1"
          aria-label="Say something"
          placeholder={`Say something to ${label}…`}
          maxLength={MAX}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" className="btn" disabled={say.isPending || !text.trim()}>Say</button>
      </form>
      )}
      <div className="flex justify-between text-xs text-ink-faint">
        <span>{text.length > MAX - 40 ? `${MAX - text.length} left` : ''}</span>
      </div>
      <ErrorNotice error={say.error ?? q.error} />
    </div>
  );
}
